
import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { debugLogger } from '@/utils/debugLogger';
import { useMultiSizeImageProcessor, ImageVariant } from './useMultiSizeImageProcessor';
import { generatePhotoPath, isNewStructureUrl } from '@/utils/photoUrlUtils';

export const useOnboardingPhotoManager = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState<number | null>(null);
  const { user, profileData } = useAuth();
  const { processImageToVariants } = useMultiSizeImageProcessor();

  debugLogger.debug('[useOnboardingPhotoManager] Hook initialized with state:', {
    isUploading,
    isDeletingPhoto,
    hasUser: !!user,
    userId: user?.id,
    username: profileData?.username
  });

  // Extract file path from Supabase URL for deletion
  const extractFilePathFromUrl = useCallback((url: string): string | null => {
    debugLogger.debug('[useOnboardingPhotoManager] ENTRY: extractFilePathFromUrl with:', url);
    
    try {
      const publicMatch = url.match(/\/storage\/v1\/object\/public\/([^\/]+)\/(.+)$/);
      if (publicMatch) {
        const filePath = publicMatch[2];
        debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: Found public URL - Path:', filePath);
        return filePath;
      }
      
      const signedMatch = url.match(/\/storage\/v1\/object\/sign\/([^\/]+)\/(.+)\?/);
      if (signedMatch) {
        const filePath = signedMatch[2];
        debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: Found signed URL - Path:', filePath);
        return filePath;
      }
      
      debugLogger.warn('[useOnboardingPhotoManager] WARNING: Could not extract file path from URL');
      return null;
    } catch (error) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: Error extracting file path:', {
        error: error?.message || 'Unknown error',
        errorObject: error
      });
      return null;
    }
  }, []);

  // Delete file from storage
  const deleteFromStorage = useCallback(async (photoUrl: string): Promise<boolean> => {
    debugLogger.debug('[useOnboardingPhotoManager] ENTRY: deleteFromStorage with:', photoUrl);
    
    try {
      const filePath = extractFilePathFromUrl(photoUrl);
      if (!filePath) {
        debugLogger.warn('[useOnboardingPhotoManager] WARNING: Could not extract file path, continuing with removal anyway');
        return true;
      }

      debugLogger.debug('[useOnboardingPhotoManager] DEBUG: Attempting to delete from storage with path:', filePath);
      
      // If it's new structure, delete all variants (support both old and new)
      if (isNewStructureUrl(photoUrl)) {
        const variants = ['ultra-thumb', 'medium-display', 'full-quality', 'micro-avatar', 'small-thumb', 'medium-card', 'large-display'];
        const basePath = filePath.replace('.jpg', '');
        
        for (const variant of variants) {
          const variantPath = `${basePath}-${variant}.jpg`;
          const { error } = await supabase.storage
            .from('profile_photos')
            .remove([variantPath]);
          
          if (error) {
            debugLogger.warn('[useOnboardingPhotoManager] WARNING: Failed to delete variant:', {
              variant,
              path: variantPath,
              error: error.message
            });
          } else {
            debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: Deleted variant:', variant);
          }
        }
      } else {
        // Legacy deletion
        const { error } = await supabase.storage
          .from('profile_photos')
          .remove([filePath]);

        if (error) {
          debugLogger.error('[useOnboardingPhotoManager] ERROR: Failed to delete from storage:', {
            error: error.message,
            errorObject: error
          });
          return true; // Continue with removal even if storage deletion fails
        } else {
          debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: Successfully deleted from storage');
        }
      }

      return true;
    } catch (error) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: Error in deleteFromStorage:', {
        error: error?.message || 'Unknown error',
        errorObject: error
      });
      return true;
    }
  }, [extractFilePathFromUrl]);

  // Upload variants and return base URL
  const uploadVariants = useCallback(async (variants: ImageVariant[], originalFileName: string): Promise<string | null> => {
    debugLogger.debug('[useOnboardingPhotoManager] ENTRY: uploadVariants called with:', {
      variantsCount: variants.length,
      originalFileName,
      variantTypes: variants.map(v => v.type)
    });

    if (!user || !profileData?.username) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: No user or username available for upload');
      return null;
    }

    try {
      // Upload all 4 variants with new structure
      const uploadResults: string[] = [];
      const timestamp = Date.now();
      
      for (const variant of variants) {
        const filePath = await generatePhotoPath(
          user.id,
          profileData.username,
          timestamp,
          variant.type
        );
        
        debugLogger.debug('[useOnboardingPhotoManager] DEBUG: Uploading variant:', {
          type: variant.type,
          filePath,
          sizeKB: Math.round(variant.actualSize / 1024),
          dimensions: variant.dimensions
        });
        
        const { data, error } = await supabase.storage
          .from('profile_photos')
          .upload(filePath, variant.file);

        if (error) {
          debugLogger.error('[useOnboardingPhotoManager] ERROR: Error uploading variant:', {
            type: variant.type,
            filePath,
            error: error.message,
            errorObject: error
          });
          throw error;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('profile_photos')
          .getPublicUrl(data.path);

        uploadResults.push(publicUrl);
        debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: Variant uploaded:', {
          type: variant.type,
          url: publicUrl,
          sizeKB: Math.round(variant.actualSize / 1024)
        });
      }

      if (uploadResults.length !== 4) {
        debugLogger.error('[useOnboardingPhotoManager] ERROR: Not all variants uploaded successfully:', {
          expected: 4,
          actual: uploadResults.length,
          uploadResults
        });
        throw new Error('Not all image variants uploaded successfully');
      }

      // Generate base URL (remove variant suffix from large-display URL)
      const largeDisplayUrl = uploadResults.find(url => url.includes('-large-display.jpg'));
      if (!largeDisplayUrl) {
        debugLogger.error('[useOnboardingPhotoManager] ERROR: Could not find large-display URL:', uploadResults);
        throw new Error('Could not find large-display URL');
      }

      const baseUrl = largeDisplayUrl.replace('-large-display.jpg', '.jpg');
      
      debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: All variants uploaded successfully:', {
        originalFileName,
        baseUrl,
        uploadedVariants: uploadResults.length,
        allUrls: uploadResults,
        totalSizeKB: Math.round(variants.reduce((sum, v) => sum + v.actualSize, 0) / 1024),
        newStructure: true,
        photoVersionToSet: 'v1' // 🎯 FASE 4: Diagnostic log
      });

      return baseUrl;

    } catch (error) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: Failed to upload variants:', {
        error: error?.message || 'Unknown error',
        errorObject: error,
        originalFileName,
        variantsCount: variants.length
      });
      throw error;
    }
  }, [user, profileData?.username]);

  // LEGACY: Upload photos (for compatibility) - now uses new structure
  const uploadPhotos = useCallback(async (files: File[]): Promise<string[]> => {
    debugLogger.debug('[useOnboardingPhotoManager] ENTRY: uploadPhotos called with:', {
      filesCount: files.length,
      fileNames: files.map(f => f.name),
      fileSizes: files.map(f => f.size),
      fileTypes: files.map(f => f.type),
      hasUser: !!user,
      userId: user?.id,
      username: profileData?.username
    });

    if (!user || !profileData?.username) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: No user or username available for upload');
      return [];
    }

    setIsUploading(true);
    debugLogger.debug('[useOnboardingPhotoManager] STATE: isUploading set to true');
    
    const uploadedUrls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        debugLogger.debug('[useOnboardingPhotoManager] DEBUG: Processing photo', i + 1, 'of', files.length, {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        });
        
        // Process into variants
        const variants = await processImageToVariants(file);
        
        // Upload all variants
        const baseUrl = await uploadVariants(variants, file.name);
        
        if (baseUrl) {
          uploadedUrls.push(baseUrl);
          debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: Successfully uploaded photo:', {
            fileName: file.name,
            baseUrl,
            totalUploadedSoFar: uploadedUrls.length
          });
        }
      }

      debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: All uploads completed:', {
        totalUploaded: uploadedUrls.length,
        uploadedUrls,
        newStructureUsed: true
      });

      return uploadedUrls;
    } catch (error) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: Error in photo upload:', {
        error: error?.message || 'Unknown error',
        errorObject: error,
        uploadedSoFar: uploadedUrls.length
      });
      throw error;
    } finally {
      setIsUploading(false);
      debugLogger.debug('[useOnboardingPhotoManager] STATE: isUploading set to false');
    }
  }, [user, profileData?.username, processImageToVariants, uploadVariants]);

  // Remove photo
  const removePhoto = useCallback(async (photoUrl: string): Promise<boolean> => {
    debugLogger.debug('[useOnboardingPhotoManager] ENTRY: removePhoto called with:', photoUrl);
    
    try {
      const result = await deleteFromStorage(photoUrl);
      debugLogger.debug('[useOnboardingPhotoManager] SUCCESS: removePhoto completed with result:', result);
      return result;
    } catch (error) {
      debugLogger.error('[useOnboardingPhotoManager] ERROR: Error removing photo:', {
        error: error?.message || 'Unknown error',
        errorObject: error,
        photoUrl
      });
      return false;
    }
  }, [deleteFromStorage]);

  return {
    isUploading,
    isDeletingPhoto,
    setIsDeletingPhoto,
    uploadPhotos,
    uploadVariants,
    removePhoto
  };
};
